import { ComponentProps, ForwardRefRenderFunction } from 'react';
import { createStitches, defaultThemeMap } from '@stitches/react';
import type * as Stitches from '@stitches/react';
import { emptyThemeDefinition } from './theme-files/empty-theme-definition';
import { typography } from './utils/typography';
import { marginX, marginY } from './utils/margins';
import { paddingX, paddingY } from './utils/padding';
import { mediaQueryMap } from '../breakpoints/media-queries-map';
import {
  pseudolocalize,
  shouldPseudolocalize,
} from '../pseudolocalize-string-children';

const themeDefinition = {
  theme: emptyThemeDefinition, // Empty theme to be overwritten with a client theme
  media: mediaQueryMap,
  utils: {
    typography,
    marginX,
    marginY,
    paddingX,
    paddingY,
  },
  themeMap: {
    ...defaultThemeMap,
    textTransform: 'textTransforms',
    textDecoration: 'textDecorations',
    marginX: 'space',
    marginY: 'space',
    paddingX: 'space',
    paddingY: 'space',
    borderStartStartRadius: 'radii',
    borderStartEndRadius: 'radii',
    borderEndStartRadius: 'radii',
    borderEndEndRadius: 'radii',
    borderBlockColor: 'colors',
    borderBlockStartColor: 'colors',
    borderBlockEndColor: 'colors',
    borderBlockWidth: 'borderWidths',
    borderBlockStartWidth: 'borderWidths',
    borderBlockEndWidth: 'borderWidths',
    borderInlineColor: 'colors',
    borderInlineStartColor: 'colors',
    borderInlineEndColor: 'colors',
    borderInlineWidth: 'borderWidths',
    borderInlineStartWidth: 'borderWidths',
    borderInlineEndWidth: 'borderWidths',
  },
  prefix: 'GDS',
};

const { styled, theme, createTheme, config, css, keyframes, globalCss } =
  createStitches(themeDefinition);

/**
 * When appropriate, replace the `styled` function from Stitches with a wrapper that pseudolocalizes
 * all string children.
 */
const transformingStyled = shouldPseudolocalize
  ? (((...args) => {
      const StyledComponent = styled(...(args as Parameters<typeof styled>));

      type WithRender = typeof StyledComponent & {
        render: ForwardRefRenderFunction<
          any,
          ComponentProps<typeof StyledComponent>
        >;
      };

      const originalRender = (StyledComponent as WithRender).render;

      (StyledComponent as WithRender).render = ({ children, ...rest }, ref) =>
        originalRender({ children: pseudolocalize(children), ...rest }, ref);

      return StyledComponent;
    }) as typeof styled)
  : styled;

export {
  transformingStyled as styled,
  theme,
  createTheme,
  config,
  css,
  keyframes,
  globalCss,
};

export type GDSStyleObject = Stitches.CSS<typeof config>;
export type GDSVariantProps<
  T extends { [key: string]: any; [key: symbol]: any },
> = Stitches.VariantProps<T>;

export type Prefixed<K extends string, T> = `${K}${Extract<T, string>}`;
export type GDSColor = Prefixed<'$', keyof typeof config.theme.colors>;
export type GDSColorToken = keyof typeof config.theme.colors;
export type GDSSpace = Prefixed<'$', keyof typeof config.theme.space>;
export type GDSSpaceToken = keyof typeof config.theme.space;
export type GDSSize = Prefixed<'$', keyof typeof config.theme.sizes>;
export type GDSBorderRadius = Prefixed<'$', keyof typeof config.theme.radii>;
export type GDSBorderWidthToken = keyof typeof config.theme.borderWidths;
export type GDSBorderWidth = Prefixed<'$', GDSBorderWidthToken>;

export type GDSStitchesTheme = typeof theme;
